import { type NsCustomer } from '../../models/ns-customer';
import { type QuoteLogEntry } from '../../models/quote-log-entry';
import {
	type Attachment,
	type Quote,
} from '../../models/quote.model';

// The catch-all the API assigns when it cannot resolve a customer from the
// email. Matching itself lives in the API now (Service/Matching), but the quote
// log still needs the name to flag those rows for the user to correct.
export const CREDIT_CARD_SALE_NAME = 'Credit Card Sale : Credit Card Sale';

import { type NsCustomerFields } from '../../models/ns-customer-fields';

export { type NsCustomerFields };

// Optimistic only. The API owns this split — it resolves the picked record and
// returns the four fields it stored, and persistNsCustomer overwrites the row
// with that response. This exists so the grid updates on click instead of
// sitting stale for a round trip, and it can be a hop behind without harm.
//
// nsCustomerId must always be the parent company id — market-basket scoping and
// item-match auditing key on it. A picked record whose parentId names a
// different record is a location: that parent becomes nsCustomerId and the pick
// becomes nsLocationId. NetSuite marks a top-level company by pointing parentId
// at the record's own id, so that case fills nsCustomerId and leaves the
// location blank until someone chooses one.
export function nsCustomerFields(
	customer: NsCustomer | null,
	allCustomers: readonly NsCustomer[]
): NsCustomerFields {
	if (!customer) {
		return {
			nsCustomerId: null,
			nsCustomerName: null,
			nsLocationId: null,
			nsLocationName: null
		};
	}

	if (!customer.parentId || customer.parentId === customer.id) {
		return {
			nsCustomerId: customer.id,
			nsCustomerName: customer.name,
			nsLocationId: null,
			nsLocationName: null
		};
	}

	const parent =
		allCustomers.find((c) => c.id === customer.parentId) ?? null;
	return {
		nsCustomerId: customer.parentId,
		nsCustomerName: parent?.name ?? null,
		nsLocationId: customer.id,
		nsLocationName: customer.name
	};
}

// The record the user picked, which is the location when there is one and the
// company otherwise. The customer dropdown binds to this: nsCustomerId is the
// parent after the split, so binding to it directly would snap the row back to
// "Apache Corporation" after someone picked "Apache Corporation : Permian-Southeast".
export function selectedNsRecordId(quote: Quote): string | null {
	// An empty string counts as "no location" too — rematch and the ns-customer PATCH
	// both write '' rather than null, and a blank id would select nothing.
	const locationId = quote.nsLocationId === '' ? null : quote.nsLocationId;
	return locationId ?? quote.nsCustomerId;
}

// Quote log timestamps arrive without a timezone marker but are already UTC, so
// they need an explicit 'Z' or Date would parse them as local time.
export function toIsoUtc(dateStr: string): string {
	return dateStr.includes('Z') ? dateStr : dateStr + 'Z';
}

export function calcHours(dueDate: string | null): number | null {
	if (!dueDate) return null;
	const diff = new Date(dueDate).getTime() - Date.now();
	return Math.round(diff / 3600000);
}

export function calcIsOverdue(dueDate: string | null): boolean {
	if (!dueDate) return false;
	return new Date(dueDate).getTime() < Date.now();
}

// True when the API could not resolve a customer from the email and fell back
// to the catch-all credit card customer
export function customerIsDefaultOrEmpty(quote: Quote): boolean {
	if (quote.nsCustomerName == null || quote.nsCustomerName === '') {
		return true;
	}
	return quote.nsCustomerName === CREDIT_CARD_SALE_NAME;
}

export function initials(name: string | null): string {
	if (!name) return '?';
	return name
		.split(' ')
		.map((n) => n[0])
		.join('')
		.toUpperCase()
		.slice(0, 2);
}

// Maps a raw API row onto the view model the quote log renders, pre-computing
// the display labels and overdue state so the template stays declarative.
export function toQuote(entry: QuoteLogEntry): Quote {
	// const threadReplies = entry.threadReplies
	// 	? (JSON.parse(entry.threadReplies) as ThreadReply[])
	// 	: [];
	const receivedIso = toIsoUtc(entry.receivedAt);
	const dueIso = entry.dueDate ? toIsoUtc(entry.dueDate) : null;
	return {
		id: String(entry.id),
		receivedLabel: new Date(receivedIso).toLocaleString(),
		dueLabel: dueIso ? new Date(dueIso).toLocaleString() : '—',
		hoursRemaining: calcHours(dueIso),
		isOverdue: calcIsOverdue(dueIso),
		status: entry.insideRepId ? 'assigned' : 'unassigned',
		customer: entry.customerName ?? '—',
		emailSummary: entry.emailSummary ?? '—',
		outsideRep: {
			name: entry.salesmanName ?? 'Unassigned',
			initials: initials(entry.salesmanName),
			avatarColor: '#0063a8'
		},
		insideRep: entry.insideRepName ?? null,
		insideRepId: entry.insideRepId ?? null,
		estimateNumber: entry.estimateNumber ?? null,
		nsCustomerId: entry.nsCustomerId ?? null,
		nsCustomerName: entry.nsCustomerName ?? null,
		nsLocationId: entry.nsLocationId ?? null,
		nsLocationName: entry.nsLocationName ?? null,
		receivedDate: entry.receivedAt,
		dueDate: entry.dueDate,
		nsEstimateId: entry.nsEstimateId ?? null,
		hasThreadReplies: entry.hasThreadReplies,
		threadReplies: entry.threadReplies ?? null,
		threadReplyCount: entry.threadReplyCount,
		emailWebLink: entry.emailWebLink ?? null,
		subject: entry.subject ?? null,
		dueDateDefaulted: entry.dueDateDefaulted,
		nsEstimateStatus: entry.nsEstimateStatus ?? null,
		nsQuoteStatus: entry.nsQuoteStatus ?? null,
		isEstimateCreating: false,
		attachments: entry.attachments
			? (JSON.parse(entry.attachments) as Attachment[])
			: [],
		attachmentsOmitted: entry.attachmentsOmitted,
		dueDateIsoUtc: entry.dueDate? new Date(toIsoUtc(entry.dueDate)) : null
	};
}
